import { useRef } from "react"
import { Formik, Field, Form } from "formik"
import {
  InputGroup,
  Label,
  Wrapper,
  CombineInputGroup,
  RupeeIcon,
  FieldWrapper,
  SubmitBtn,
} from "../Styles/InputStyles"
import { toast } from "react-toastify"
import tw from "tailwind-styled-components"
import * as Yup from "yup";

const PropertyFinance = ({
  editInventoryFinanceLoading,
  editInventoryFinanceMutate,
  apiData,
}) => {

  const formikRef = useRef(null)

  const initialValues = {
    listingType: apiData?.property?.listingType || "RENT",

    // RENT
    amount: apiData?.amount || "",
    deposit: apiData?.deposit || "",
    depositMonths: apiData?.depositMonths || "",
    maintenance: apiData?.maintenance || "",
    moveInCharges: apiData?.moveInCharges || "",
    moveOutCharges: apiData?.moveOutCharges || "",

    // SALE
    salePrice: apiData?.salePrice || "",
    advanceAmount: apiData?.advanceAmount || "",
    tokenAmount: apiData?.tokenAmount || "",
  }

  const validationSchema = Yup.object().shape({
    listingType: Yup.string().required("Listing type is required"),

    amount: Yup.number().when("listingType", {
      is: "RENT",
      then: (schema) =>
        schema
          .typeError("Enter valid rent")
          .required("Monthly rent is required")
          .min(500, "Rent must be at least ₹500")
          .max(1000000, "Rent cannot exceed ₹10,00,000"),
      otherwise: (schema) => schema.notRequired(),
    }),

    deposit: Yup.number()
      .nullable()
      .typeError("Enter valid security deposit")
      .min(0, "Deposit cannot be negative")
      .max(5000000, "Deposit cannot exceed ₹50,00,000"),

    depositMonths: Yup.number()
      .nullable()
      .typeError("Enter valid months")
      .min(0, "Months cannot be negative")
      .max(12, "Maximum 12 months allowed"),

    maintenance: Yup.number()
      .nullable()
      .min(0, "Cannot be negative")
      .max(1000000, "Maintenance cannot exceed ₹10,00,000"),

    moveInCharges: Yup.number()
      .nullable()
      .min(0, "Cannot be negative")
      .max(1000000, "MoveIn Charge cannot exceed ₹10,00,000"),

    moveOutCharges: Yup.number()
      .nullable()
      .min(0, "Cannot be negative")
      .max(1000000, "MoveOut Charge cannot exceed ₹10,00,000"),
  });

  const handleSubmit = (values) => {

    if (values.listingType === "RENT" && !values.amount) {
      toast.error("Monthly rent is required")
      return
    }

    if (values.listingType === "SALE" && !values.salePrice) {
      toast.error("Sale price is required")
      return
    }

    editInventoryFinanceMutate({
      inventoryId: apiData?.id,
      listingType: values.listingType,

      // RENT PAYLOAD
      amount: values.amount || "",
      deposit: values.deposit || "",
      depositMonths: values.depositMonths || "",
      maintenance: values.maintenance || "",
      moveInCharges: values.moveInCharges || "",
      moveOutCharges: values.moveOutCharges || "",
    })
  }

  // const inputClass =
  //   "pl-2 rounded-md w-full border border-gray-400 h-12"
  const inputClass =
    "w-full border-none outline-none h-12 bg-transparent pl-5 flex items-center";

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, errors, touched }) => (
        <Form>
          <Wrapper>

            <CombineInputGroup>
              <InputGroup>
                <Label>Listing Type</Label>

                <ListingToggle>

                  <ToggleItem
                    type="button"
                    onClick={() => setFieldValue("listingType", "RENT")}
                    className={`${values.listingType === "RENT"
                      ? "bg-white shadow-sm text-black"
                      : "text-gray-500"
                      }`}
                  >
                    Rent
                  </ToggleItem>

                  <ToggleItem
                    type="button"
                    onClick={() => setFieldValue("listingType", "SALE")}
                    className={`${values.listingType === "SALE"
                      ? "bg-white shadow-sm text-black"
                      : "text-gray-500"
                      }`}
                    disabled
                  >
                    Sale
                  </ToggleItem>

                </ListingToggle>

              </InputGroup>
            </CombineInputGroup>

            {/* ================= RENT ================= */}
            {values.listingType === "RENT" && (
              <>
                <CombineInputGroup>
                  <InputGroup>
                    <Label>Per Month Rent *</Label>
                    <FieldWrapper >
                      <RupeeIcon />
                      <Field
                        type="number"
                        name="amount"
                        className={inputClass}
                      />
                    </FieldWrapper>
                    <div className="min-h-[20px] mt-1">
                      {touched.amount && errors.amount && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.amount}
                        </p>
                      )}
                    </div>
                  </InputGroup>

                  <InputGroup>
                    <Label>Security Deposit</Label>
                    <FieldWrapper>
                      <RupeeIcon />
                      <Field
                        type="number"
                        name="deposit"
                        className={inputClass}
                      />
                    </FieldWrapper>
                    <div className="min-h-[20px] mt-1">
                      {touched.deposit && errors.deposit && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.deposit}
                        </p>
                      )}
                    </div>
                  </InputGroup>

                  <InputGroup>
                    <Label>Security (Months)</Label>
                    <FieldWrapper>
                      <Field
                        type="number"
                        name="depositMonths"
                        className={inputClass}
                      />
                    </FieldWrapper>
                    <div className="min-h-[20px] mt-1">
                      {touched.depositMonths && errors.depositMonths && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.depositMonths}
                        </p>
                      )}
                    </div>
                  </InputGroup>
                </CombineInputGroup>

                <CombineInputGroup>
                  <InputGroup>
                    <Label>Maintenance Charges</Label>
                    <FieldWrapper>
                      <RupeeIcon />
                      <Field
                        type="number"
                        name="maintenance"
                        className={inputClass}
                      />
                    </FieldWrapper>
                    <div className="min-h-[20px] mt-1">
                      {touched.maintenance && errors.maintenance && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.maintenance}
                        </p>
                      )}
                    </div>
                  </InputGroup>

                  <InputGroup>
                    <Label>Society Move-In</Label>
                    <FieldWrapper>
                      <RupeeIcon />
                      <Field
                        type="number"
                        name="moveInCharges"
                        className={inputClass}
                      />
                    </FieldWrapper>
                    <div className="min-h-[20px] mt-1">
                      {touched.moveInCharges && errors.moveInCharges && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.moveInCharges}
                        </p>
                      )}
                    </div>
                  </InputGroup>

                  <InputGroup>
                    <Label>Society Move-Out</Label>
                    <FieldWrapper>
                      <RupeeIcon />
                      <Field
                        type="number"
                        name="moveOutCharges"
                        className={inputClass}
                      />
                    </FieldWrapper>
                    <div className="min-h-[20px] mt-1">
                      {touched.moveOutCharges && errors.moveOutCharges && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.moveOutCharges}
                        </p>
                      )}
                    </div>
                  </InputGroup>
                </CombineInputGroup>
              </>
            )}

            {/* ================= SALE ================= */}
            {values.listingType === "SALE" && (
              <>
                <CombineInputGroup>
                  <InputGroup>
                    <Label>Sale Price *</Label>
                    <Field
                      type="number"
                      name="salePrice"
                      className={inputClass}
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Advance / Booking Amount</Label>
                    <Field
                      type="number"
                      name="advanceAmount"
                      className={inputClass}
                    />
                  </InputGroup>

                  <InputGroup>
                    <Label>Token Amount</Label>
                    <Field
                      type="number"
                      name="tokenAmount"
                      className={inputClass}
                    />
                  </InputGroup>
                </CombineInputGroup>
              </>
            )}

          </Wrapper>

          <BtnWrapper>
            <SubmitBtn 
               type="submit" 
               disabled={editInventoryFinanceLoading}
               className="text-sm  w-24  md:w-20 h-7 md:text-xs"
            >
              {editInventoryFinanceLoading ? "Updating..." : "Update"}
            </SubmitBtn>
          </BtnWrapper>

        </Form>
      )}
    </Formik>
  )
}

const BtnWrapper = tw.div`w-full flex items-center justify-end mt-4`
const ListingToggle = tw.div`
flex w-fit rounded-lg bg-gray-100 p-1 border border-gray-200
`
const ToggleItem = tw.button`
px-8 h-9 rounded-md text-sm font-medium transition-all duration-200
`
export default PropertyFinance